'use client'

import type { ReactNode } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import { tw } from '@/lib/utils'

import { ScorecardBuyingSummary } from './scorecard-buying-summary'
import { ScorecardTestedCaption } from './scorecard-tested-caption'
import { ScorecardV2Header } from './scorecard-v2-header'
import { ScorecardV2ListenLink } from './scorecard-v2-listen-link'
import { ScorecardV2Verdict } from './scorecard-v2-verdict'

import type { ScorecardData } from '../scorecard-types'

/** The offer stays visible; only the editorial area scrolls on short desktop screens. */
export const ScorecardV2 = ({
  data,
  className,
  familyProductCount,
  description,
  glossaryPanel,
}: {
  data: ScorecardData
  className?: string
  familyProductCount: number
  description?: ReactNode
  glossaryPanel?: ReactNode
}) => {
  const { t } = useTranslation()
  return (
    <section
      data-scorecard-variant="adaptive"
      data-glossary-scope={glossaryPanel ? '' : undefined}
      className={tw(
        'product-scorecard flex w-full min-w-0 flex-col overflow-hidden rounded-xl bg-navy text-white product-sidebar:max-h-[calc(100svh-128px)]',
        className
      )}
    >
      {glossaryPanel}
      <div
        data-scorecard-editorial
        tabIndex={0}
        className="min-h-0 space-y-3 px-6 pt-6 pb-4 scheme-dark [scrollbar-width:thin] focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-sky product-sidebar:overflow-y-auto"
      >
        <ScorecardV2Header data={data} />
        {data.description && <ScorecardV2Verdict description={description ?? data.description} />}
        {data.testedConfiguration && (
          <ScorecardTestedCaption
            configuration={data.testedConfiguration}
            discontinued={data.discontinued}
            familyProductCount={familyProductCount}
          />
        )}
        {data.discontinued && (
          <p className="text-sm text-white/65">{t('product.purchase.discontinued', 'Discontinued')}</p>
        )}
        <div>
          {data.quietAudioUrl && (
            <ScorecardV2ListenLink
              url={data.quietAudioUrl}
              group="product-review"
              className="h-7 text-sm text-sky hover:text-white"
            />
          )}
        </div>
      </div>
      {!data.discontinued && <ScorecardBuyingSummary />}
    </section>
  )
}
